import { Typography } from '@/components/atoms';
import { ArrowRightIcon } from '@/components/atoms/icons';
import { getColors } from '@/core/constants/uiColors';
import { useWebViewport } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
  nativeLtrIsolationStyle,
  webViewDirProps,
} from '@/core/utils/layoutDirection';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useMemo } from 'react';
import {
  ImageBackground,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type DimensionValue,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import type { BloomSpaceGridSpan } from '../types/bloomUniversalContent';
import { resolveBloomUniversalImageUrl } from '../utils/resolveImageUrl';
import { BloomCmsIcon } from './BloomCmsIcon';

const bloomColors = getColors();

const PHONE_MAX = 768;
const TABLET_MAX = 1100;
/** Figma Container / detail cards — 18px */
const DEFAULT_RADIUS = 18;
/** Mobile hub mosaic cards (small screen). */
const PHONE_RADIUS = 8;
/** Figma artboard width used to scale type with viewport */
const FIGMA_WIDTH = 1440;

/** Font/Size/2XL — full-width hub tile (e.g. Bloom Landscape). */
const FONT_SIZE_2XL = 28;
/** Font/Size/L — third / half / wide tiles (e.g. Bloom Properties). */
const FONT_SIZE_L = 20;

/** Figma card overlay: 180deg transparent → #1C1C19 @ 85% (hub `action` only) */
const CARD_GRADIENT_COLORS = [
  bloomColors.bloomUniversalActionScrimStart,
  bloomColors.bloomUniversalActionScrimEnd,
] as const;
const CARD_GRADIENT_LOCATIONS = [0, 1] as const;

/** Match hub phone breakpoint (BloomUniversalHubScreen). */
const HUB_PHONE_MAX = 700;
/** Medium hub ends where large Figma desktop begins. */
const HUB_LARGE_MIN = 1440;
/** Tighter phone mosaic inset — icon sits closer to top-right. */
const PHONE_ACTION_INSET = 8;
/** Space for bottom-left arrow so RTL titles do not run under the glyph. */
const RTL_ARROW_TITLE_INSET = 28;
/** Type badge sits slightly tighter than footer text inset. */
const PHONE_TYPE_ICON_INSET = 6;
/** Medium mosaic: keep icons closer to the corner than footer padding. */
const MEDIUM_TYPE_ICON_INSET = 10;
/** Medium mosaic default content inset (footer / title stack). */
const MEDIUM_ACTION_INSET = 12;
/**
 * Hub mosaic (`action`) inset — same on every side (Figma Spacing/4).
 * Does not apply to `detail` space-page cards.
 */
const ACTION_CARD_INSET = 25;

/**
 * Detail cards — mid/top clear photo; one continuous bottom scrim (no solid band edge).
 * Text sits on the opaque end of the gradient so there’s no hard line.
 */
const DETAIL_SCRIM_COLOR = bloomColors.bloomUniversalDetailScrim;
const DETAIL_GRADIENT_HEIGHT = 130;
const DETAIL_GRADIENT_COLORS = [
  bloomColors.bloomUniversalDetailScrimStart,
  bloomColors.bloomUniversalDetailScrimEnd,
  DETAIL_SCRIM_COLOR,
  DETAIL_SCRIM_COLOR,
] as const;
const DETAIL_GRADIENT_LOCATIONS = [0, 0.4, 0.72, 1] as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function resolveTypeIconBadgeSize(
  isHubPhoneWeb: boolean,
  isHubMediumWeb: boolean,
  isPhone: boolean,
): number {
  if (isHubPhoneWeb) {
    return 28;
  }
  if (isHubMediumWeb) {
    return 30;
  }
  if (isPhone) {
    return 32;
  }
  return 36;
}

function resolveTypeIconGlyphSize(
  isHubPhoneWeb: boolean,
  isHubMediumWeb: boolean,
  isPhone: boolean,
): number {
  if (isHubPhoneWeb || isHubMediumWeb || isPhone) {
    return 14;
  }
  return 16;
}

function resolveDefaultCardHeight(
  heightProp: number | undefined,
  isDetail: boolean,
  isPhone: boolean,
  gridSpan: BloomSpaceGridSpan | undefined,
): number {
  if (heightProp != null) {
    return heightProp;
  }
  if (isDetail) {
    return isPhone ? 180 : 210;
  }
  if (isPhone) {
    return 160;
  }
  if (gridSpan === 'full') {
    return 168;
  }
  return 180;
}

function resolveHubActionInset(
  contentInset: number,
  isHubPhoneWeb: boolean,
  isHubMediumWeb: boolean,
): number {
  if (isHubPhoneWeb) {
    return contentInset === ACTION_CARD_INSET ? PHONE_ACTION_INSET : contentInset;
  }
  if (!isHubMediumWeb) {
    return contentInset;
  }
  if (contentInset === ACTION_CARD_INSET) {
    return MEDIUM_ACTION_INSET;
  }
  return Math.min(contentInset, MEDIUM_ACTION_INSET);
}

function phoneWebTitleStyle(keepAll: boolean): object | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  return {
    overflowWrap: 'normal',
    wordBreak: keepAll ? 'keep-all' : 'normal',
    hyphens: 'none',
  } as object;
}

/**
 * Hub mosaic title size from Figma:
 * full → Size/2XL Medium (28); other spans → Size/L Medium (20).
 * Scales with viewport so it holds on all screen sizes.
 */
function resolveActionTitleSize(
  span: BloomSpaceGridSpan | undefined,
  windowWidth: number,
  isPhone: boolean,
): { fontSize: number; lineHeight: number } {
  const base = span === 'full' ? FONT_SIZE_2XL : FONT_SIZE_L;

  if (isPhone) {
    const phoneSize = Math.max(16, Math.round(base * 0.75));
    return { fontSize: phoneSize, lineHeight: phoneSize };
  }
  const typeScale = clamp(windowWidth / FIGMA_WIDTH, 0.72, 1.0);
  const fontSize = Math.round(base * typeScale);
  return { fontSize, lineHeight: fontSize };
}

/**
 * `action` — hub mosaic tile: span-based title sizes, type icon, equal insets,
 * arrow, border / shadow / gradient from hub Figma.
 * `detail` — space-page tile: title + optional subtitle, bottom gradient only.
 */
export type BloomSpaceCardVariant = 'action' | 'detail';

export interface BloomSpaceCardProps {
  readonly title: string;
  /** Second line under title — used by `detail` variant. */
  readonly subtitle?: string;
  readonly variant?: BloomSpaceCardVariant;
  /** Space id for navigation / mosaic keying (`landscape`, `properties`, …). */
  readonly spaceKey?: string;
  /** Contentful space `icon` URL — required to show the type badge. */
  readonly iconUrl?: string;
  /** Contentful / JSON asset URL (`tileImage.url`). */
  readonly tileImageUrl?: string;
  /** Contentful hub `arrowIcon` — falls back to app ArrowRightIcon. */
  readonly arrowIconUrl?: string;
  /**
   * Explicit width (px or %). When set, overrides `gridSpan`.
   * Omit both and the card stretches to parent (`100%`).
   */
  readonly width?: DimensionValue;
  /** Explicit height in px. */
  readonly height?: number;
  /** Mosaic span helper when `width` is omitted (hub layout). */
  readonly gridSpan?: BloomSpaceGridSpan;
  /** Outer padding when used in a mosaic row (half gutter). */
  readonly gutter?: number;
  readonly borderRadius?: number;
  /** Override hub mosaic title size (e.g. larger type on medium web). */
  readonly titleFontSize?: number;
  /** Detail-card subtitle size. Line height matches the size. */
  readonly subtitleFontSize?: number;
  /** Override action-card inner inset (default 25 / Figma Spacing/4). */
  readonly contentInset?: number;
  /**
   * Hub mosaic title placement:
   * - `mid` — title mid-card (tall + narrow tiles)
   * - `bottom` — original bottom footer (tiles wider than the medium tall card)
   */
  readonly titlePlacement?: 'mid' | 'bottom';
  /**
   * Phone mosaic only: vertical start of title as fraction of card height (0–1).
   * Default ~0.45 (mid). Used when `titlePlacement` is `mid`.
   */
  readonly titleTopPercent?: number;
  /** Phone mosaic title line cap used for maxHeight (default 2; never ellipsis). */
  readonly titleMaxLines?: number;
  /**
   * Phone tall card (Figma): column layout with this gap between title and arrow.
   * When set, replaces absolute mid/bottom placement.
   */
  readonly titleArrowGap?: number;
  /**
   * Extra space under the stacked footer arrow (lifts arrow off the card edge).
   * Defaults to `contentInset` when omitted.
   */
  readonly footerBottomInset?: number;
  /** Defaults: `true` for `action`, `false` for `detail`. */
  readonly showArrow?: boolean;
  /** Defaults: `true` for `action` when `spaceKey` is set. */
  readonly showTypeIcon?: boolean;
  readonly onPress?: () => void;
  readonly style?: StyleProp<ViewStyle>;
}

function resolveGridWidth(
  span: BloomSpaceGridSpan | undefined,
  isPhone: boolean,
  isTablet: boolean,
): DimensionValue {
  if (!span || isPhone) {
    return '100%';
  }
  if (isTablet) {
    return span === 'full' ? '100%' : '50%';
  }
  switch (span) {
    case 'full':
      return '100%';
    case 'wide':
      return '66.666%';
    case 'half':
      return '50%';
    case 'third':
      return '33.333%';
    case 'quarter':
    case 'tall':
      return '25%';
    default:
      return '50%';
  }
}

const STACK_ARROW_SIZE = 13;

function ActionFooterPhoneArrows({
  showArrow,
  isRTL,
  contentInset,
  bottomPad,
  pinArrowToBottom,
  arrowGlyph,
}: Readonly<{
  showArrow: boolean;
  isRTL: boolean;
  contentInset: number;
  bottomPad: number;
  /** Tall cards raise the title; the arrow stays on the card’s bottom edge. */
  pinArrowToBottom: boolean;
  arrowGlyph: React.ReactNode;
}>) {
  if (!showArrow) {
    return null;
  }
  if (isRTL || pinArrowToBottom) {
    const arrowBottom = pinArrowToBottom ? contentInset : bottomPad;
    return (
      <View
        pointerEvents="none"
        style={[
          styles.phoneArrowPinned,
          isRTL
            ? { left: contentInset, bottom: arrowBottom }
            : { right: contentInset, bottom: arrowBottom },
        ]}
      >
        {isRTL ? <View style={styles.arrowFlip}>{arrowGlyph}</View> : arrowGlyph}
      </View>
    );
  }
  return <View style={styles.phoneStackArrow}>{arrowGlyph}</View>;
}

function ActionFooterPhone({
  title,
  isRTL,
  showArrow,
  titleMaxLines,
  titleStyle,
  contentInset,
  bottomPad,
  stackGap,
  footerStyle,
  arrowIconUrl,
}: Readonly<{
  title: string;
  isRTL: boolean;
  showArrow: boolean;
  titleMaxLines: number;
  titleStyle: StyleProp<TextStyle>;
  contentInset: number;
  bottomPad: number;
  stackGap: number;
  footerStyle: 'stack' | 'bottom';
  arrowIconUrl?: string;
}>) {
  const arrowGlyph = (
    <BloomCmsIcon
      url={arrowIconUrl}
      size={13}
      fallback={<ArrowRightIcon size={13} color={bloomColors.bloomUniversalOnPhoto} />}
    />
  );
  const pinArrowToBottom = footerStyle === 'stack';
  const footerHostStyle = pinArrowToBottom
    ? styles.phoneStackFooter
    : styles.phoneBottomStackFooter;
  /**
   * The pinned arrow is out of flow. Keep the title above it by at least the
   * arrow size plus the designed gap, even when the title inset is smaller.
   */
  const paddingBottom = pinArrowToBottom
    ? Math.max(bottomPad, contentInset + STACK_ARROW_SIZE + stackGap)
    : bottomPad;

  return (
    <View
      pointerEvents={footerStyle === 'bottom' ? 'none' : undefined}
      {...(isRTL ? webViewDirProps('ltr') : {})}
      style={[
        footerHostStyle,
        pinArrowToBottom && isRTL ? styles.phoneFooterRtlHost : null,
        nativeLtrIsolationStyle(isRTL),
        {
          paddingTop: contentInset,
          paddingBottom,
          paddingLeft: contentInset,
          paddingRight: contentInset,
          gap: isRTL || pinArrowToBottom ? 0 : stackGap,
        },
      ]}
    >
      <Typography
        variant="title2"
        color="white"
        numberOfLines={titleMaxLines}
        style={[
          titleStyle,
          footerStyle === 'stack' ? styles.phoneStackTitle : null,
        ]}
      >
        {title}
      </Typography>
      <ActionFooterPhoneArrows
        showArrow={showArrow}
        isRTL={isRTL}
        contentInset={contentInset}
        bottomPad={bottomPad}
        pinArrowToBottom={pinArrowToBottom}
        arrowGlyph={arrowGlyph}
      />
    </View>
  );
}

function ActionFooterHub({
  title,
  isRTL,
  showArrow,
  fontSize,
  lineHeight,
  contentInset,
  arrowIconUrl,
}: Readonly<{
  title: string;
  isRTL: boolean;
  showArrow: boolean;
  fontSize: number;
  lineHeight: number;
  contentInset: number;
  arrowIconUrl?: string;
}>) {
  const titleAlign = isRTL ? ('right' as const) : ('left' as const);
  return (
    <View
      style={[
        styles.actionFooter,
        { padding: contentInset },
        isRTL ? styles.actionFooterRtl : null,
      ]}
    >
      <Typography
        variant="title2"
        color="white"
        numberOfLines={2}
        style={[
          styles.actionTitle,
          { fontSize, lineHeight, minHeight: lineHeight, textAlign: titleAlign },
          isRTL && showArrow ? { paddingLeft: RTL_ARROW_TITLE_INSET } : null,
        ]}
      >
        {title}
      </Typography>
      {showArrow ? (
        <View
          style={
            isRTL
              ? [
                  styles.arrowLeft,
                  styles.arrowFlip,
                  { left: contentInset, bottom: contentInset },
                ]
              : undefined
          }
        >
          <BloomCmsIcon
            url={arrowIconUrl}
            size={13}
            fallback={<ArrowRightIcon size={13} color={bloomColors.bloomUniversalOnPhoto} />}
          />
        </View>
      ) : null}
    </View>
  );
}

function ActionFooter({
  title,
  isRTL,
  showArrow,
  fontSize,
  lineHeight,
  contentInset,
  phoneLayout,
  titleTopPercent: _titleTopPercent,
  titleMaxLines,
  titleArrowGap,
  footerBottomInset,
  arrowIconUrl,
}: Readonly<{
  title: string;
  isRTL: boolean;
  showArrow: boolean;
  fontSize: number;
  lineHeight: number;
  contentInset: number;
  /**
   * Phone mosaic (Figma mobile cards):
   * title left, slightly below mid-height; arrow pinned bottom-right.
   */
  phoneLayout: boolean;
  titleTopPercent: number;
  titleMaxLines: number;
  titleArrowGap?: number;
  footerBottomInset?: number;
  arrowIconUrl?: string;
}>) {
  const bottomPad = footerBottomInset ?? contentInset;
  const titleAlign = isRTL ? ('right' as const) : ('left' as const);
  const rtlArrowTitlePad =
    showArrow && isRTL ? { paddingLeft: RTL_ARROW_TITLE_INSET } : null;
  const titleStyle = [
    styles.actionTitle,
    styles.actionTitlePhone,
    { fontSize, lineHeight, textAlign: titleAlign },
    rtlArrowTitlePad,
    phoneWebTitleStyle(titleArrowGap != null),
  ];

  if (phoneLayout) {
    return (
      <ActionFooterPhone
        title={title}
        isRTL={isRTL}
        showArrow={showArrow}
        titleMaxLines={titleMaxLines}
        titleStyle={titleStyle}
        contentInset={contentInset}
        bottomPad={titleArrowGap != null ? bottomPad : contentInset}
        stackGap={titleArrowGap ?? 10}
        footerStyle={titleArrowGap != null ? 'stack' : 'bottom'}
        {...(arrowIconUrl ? { arrowIconUrl } : {})}
      />
    );
  }

  return (
    <ActionFooterHub
      title={title}
      isRTL={isRTL}
      showArrow={showArrow}
      fontSize={fontSize}
      lineHeight={lineHeight}
      contentInset={contentInset}
      {...(arrowIconUrl ? { arrowIconUrl } : {})}
    />
  );
}

function DetailFooter({
  title,
  subtitle,
  titleFontSize,
  subtitleFontSize,
}: Readonly<{
  title: string;
  subtitle?: string;
  titleFontSize?: number;
  subtitleFontSize?: number;
}>) {
  const { isRTL } = useI18nContext();
  const rtlType = isRTL
    ? ({ textAlign: 'right' as const, alignSelf: 'stretch' as const })
    : null;

  return (
    <View
      style={[
        styles.detailFooterBand,
        isRTL ? { alignItems: 'flex-end' } : null,
        nativeLtrIsolationStyle(isRTL),
      ]}
      {...(isRTL ? webViewDirProps('ltr') : {})}
    >
      <Typography
        variant="headline"
        numberOfLines={2}
        style={[
          styles.detailTitle,
          titleFontSize != null
            ? {
                fontSize: titleFontSize,
                lineHeight: Math.max(
                  titleFontSize + 4,
                  Math.round(titleFontSize * 1.2),
                ),
              }
            : null,
          rtlType,
        ]}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant="subhead"
          numberOfLines={2}
          style={[
            styles.detailSubtitle,
            subtitleFontSize != null
              ? { fontSize: subtitleFontSize, lineHeight: subtitleFontSize }
              : null,
            rtlType,
          ]}
        >
          {subtitle}
        </Typography>
      ) : null}
    </View>
  );
}

function resolveHubLayoutFlags(
  windowWidth: number,
  isDetail: boolean,
): Readonly<{
  isPhone: boolean;
  isTablet: boolean;
  isHubPhoneWeb: boolean;
  isHubMediumWeb: boolean;
}> {
  const isPhone = windowWidth < PHONE_MAX;
  const isTablet = windowWidth >= PHONE_MAX && windowWidth < TABLET_MAX;
  const isHubPhoneWeb =
    Platform.OS === 'web' && windowWidth < HUB_PHONE_MAX && !isDetail;
  const isHubMediumWeb =
    Platform.OS === 'web' &&
    windowWidth >= HUB_PHONE_MAX &&
    windowWidth < HUB_LARGE_MIN &&
    !isDetail;
  return { isPhone, isTablet, isHubPhoneWeb, isHubMediumWeb };
}

function resolveUseMidTitleLayout(
  isDetail: boolean,
  titlePlacement: 'mid' | 'bottom' | undefined,
  isHubPhoneWeb: boolean,
  titleArrowGap: number | undefined,
): boolean {
  if (isDetail) {
    return false;
  }
  if (titlePlacement === 'mid') {
    return true;
  }
  if (titlePlacement != null) {
    return false;
  }
  return isHubPhoneWeb || titleArrowGap != null;
}

function resolveTypeIconInset(
  resolvedInset: number,
  isHubPhoneWeb: boolean,
  isHubMediumWeb: boolean,
): number {
  if (isHubPhoneWeb) {
    return Math.min(resolvedInset, PHONE_TYPE_ICON_INSET);
  }
  if (isHubMediumWeb) {
    return Math.min(resolvedInset, MEDIUM_TYPE_ICON_INSET);
  }
  return resolvedInset;
}

function resolveCardRadius(
  borderRadius: number,
  isHubPhoneWeb: boolean,
): number {
  if (isHubPhoneWeb && borderRadius === DEFAULT_RADIUS) {
    return PHONE_RADIUS;
  }
  return borderRadius;
}

function DetailCardOverlay({
  title,
  subtitle,
  titleFontSize,
  subtitleFontSize,
}: Readonly<{
  title: string;
  subtitle?: string;
  titleFontSize?: number;
  subtitleFontSize?: number;
}>) {
  return (
    <>
      <View pointerEvents="none" style={styles.detailInsetHighlight} />
      <LinearGradient
        pointerEvents="none"
        colors={DETAIL_GRADIENT_COLORS}
        locations={DETAIL_GRADIENT_LOCATIONS}
        style={styles.detailGradient}
      />
      <DetailFooter
        title={title}
        {...(subtitle ? { subtitle } : {})}
        {...(titleFontSize != null ? { titleFontSize } : {})}
        {...(subtitleFontSize != null ? { subtitleFontSize } : {})}
      />
    </>
  );
}

function ActionCardOverlay({
  title,
  isRTL,
  withArrow,
  fontSize,
  lineHeight,
  contentInset,
  useMidTitleLayout,
  titleTopPercent,
  titleMaxLines,
  titleArrowGap,
  footerBottomInset,
  typeIcon,
  arrowIconUrl,
}: Readonly<{
  title: string;
  isRTL: boolean;
  withArrow: boolean;
  fontSize: number;
  lineHeight: number;
  contentInset: number;
  useMidTitleLayout: boolean;
  titleTopPercent: number;
  titleMaxLines: number;
  titleArrowGap?: number;
  footerBottomInset?: number;
  typeIcon: React.ReactNode;
  arrowIconUrl?: string;
}>) {
  const footerExtras: {
    titleArrowGap?: number;
    footerBottomInset?: number;
  } = {};
  if (titleArrowGap != null) {
    footerExtras.titleArrowGap = titleArrowGap;
  }
  if (footerBottomInset != null) {
    footerExtras.footerBottomInset = footerBottomInset;
  }

  return (
    <>
      <LinearGradient
        pointerEvents="none"
        colors={CARD_GRADIENT_COLORS}
        locations={CARD_GRADIENT_LOCATIONS}
        style={styles.cardGradient}
      />
      {typeIcon}
      <ActionFooter
        title={title}
        isRTL={isRTL}
        showArrow={withArrow}
        fontSize={fontSize}
        lineHeight={lineHeight}
        contentInset={contentInset}
        phoneLayout={useMidTitleLayout}
        titleTopPercent={titleTopPercent}
        titleMaxLines={useMidTitleLayout ? titleMaxLines : 2}
        {...(arrowIconUrl ? { arrowIconUrl } : {})}
        {...footerExtras}
      />
    </>
  );
}

/**
 * Frosted type badge for hub mosaic cards.
 * Contentful `icon` only — local SVG fallbacks live in `BloomSpaceTypeIcon`
 * (exported for non-CMS / native consumers; not used on this web mosaic path).
 */
function SpaceTypeIconBadge({
  iconUrl,
  isRTL,
  typeIconInset,
  glyphSize,
  badgeSize,
}: Readonly<{
  iconUrl: string;
  isRTL: boolean;
  typeIconInset: number;
  glyphSize: number;
  badgeSize: number;
}>) {
  return (
    <View
      style={[
        styles.typeIconSlot,
        isRTL
          ? { top: typeIconInset, left: typeIconInset }
          : { top: typeIconInset, right: typeIconInset },
      ]}
      pointerEvents="none"
    >
      <View
        style={[
          styles.typeIconBadge,
          {
            width: badgeSize,
            height: badgeSize,
            borderRadius: badgeSize / 2,
          },
        ]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <BloomCmsIcon url={iconUrl} size={glyphSize} fallback={null} />
      </View>
    </View>
  );
}

function CardChrome({
  title,
  cardHeight,
  isDetail,
  isHubPhoneWeb,
  radiusStyle,
  cardBody,
  onPress,
}: Readonly<{
  title: string;
  cardHeight: number;
  isDetail: boolean;
  isHubPhoneWeb: boolean;
  radiusStyle: { borderRadius: number };
  cardBody: React.ReactNode;
  onPress?: () => void;
}>) {
  /**
   * WebKit (iPad Chrome) ignores border-radius clipping inside a scroll parent,
   * so the photo paints as a square and a grey edge shows in the corner.
   */
  const webFaceClip =
    Platform.OS === 'web'
      ? ({
          backgroundColor: 'transparent',
          clipPath: `inset(0 round ${radiusStyle.borderRadius}px)`,
        } as ViewStyle)
      : null;
  const chromeStyle = [
    styles.cardChrome,
    isDetail ? styles.detailChrome : styles.actionChrome,
    isHubPhoneWeb ? styles.actionChromePhone : null,
    radiusStyle,
    webFaceClip,
    { height: cardHeight },
  ];
  const shadowStyle = [
    isDetail ? styles.detailShadowHost : styles.actionShadowHost,
    radiusStyle,
    { height: cardHeight },
  ];

  if (onPress) {
    return (
      <View style={shadowStyle}>
        <Pressable
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel={title}
          style={({ pressed }) => [
            ...chromeStyle,
            pressed ? styles.pressed : null,
          ]}
        >
          {cardBody}
        </Pressable>
      </View>
    );
  }

  return (
    <View style={shadowStyle}>
      <View
        accessibilityRole="image"
        accessibilityLabel={title}
        style={chromeStyle}
      >
        {cardBody}
      </View>
    </View>
  );
}

/**
 * Configurable Bloom Universal image card — any width/height, two variants.
 */
function resolveSpaceCardOverlay(params: Readonly<{
  isDetail: boolean;
  title: string;
  subtitle?: string;
  titleFontSize?: number;
  subtitleFontSize?: number;
  isRTL: boolean;
  withArrow: boolean;
  titleType: { fontSize: number; lineHeight: number };
  resolvedInset: number;
  useMidTitleLayout: boolean;
  titleTopPercent: number;
  titleMaxLines: number;
  titleArrowGap?: number;
  footerBottomInset?: number;
  typeIcon: React.ReactNode;
  arrowIconUrl?: string;
}>): React.ReactNode {
  if (params.isDetail) {
    return (
      <DetailCardOverlay
        title={params.title}
        {...(params.subtitle ? { subtitle: params.subtitle } : {})}
        {...(params.titleFontSize != null
          ? { titleFontSize: params.titleFontSize }
          : {})}
        {...(params.subtitleFontSize != null
          ? { subtitleFontSize: params.subtitleFontSize }
          : {})}
      />
    );
  }

  return (
    <ActionCardOverlay
      title={params.title}
      isRTL={params.isRTL}
      withArrow={params.withArrow}
      fontSize={params.titleType.fontSize}
      lineHeight={params.titleType.lineHeight}
      contentInset={params.resolvedInset}
      useMidTitleLayout={params.useMidTitleLayout}
      titleTopPercent={params.titleTopPercent}
      titleMaxLines={params.titleMaxLines}
      {...(params.titleArrowGap != null
        ? { titleArrowGap: params.titleArrowGap }
        : {})}
      {...(params.footerBottomInset != null
        ? { footerBottomInset: params.footerBottomInset }
        : {})}
      typeIcon={params.typeIcon}
      {...(params.arrowIconUrl ? { arrowIconUrl: params.arrowIconUrl } : {})}
    />
  );
}

export function BloomSpaceCard({
  title,
  subtitle,
  variant = 'action',
  spaceKey,
  iconUrl,
  tileImageUrl,
  arrowIconUrl,
  width: widthProp,
  height: heightProp,
  gridSpan,
  gutter = 0,
  borderRadius = DEFAULT_RADIUS,
  titleFontSize,
  subtitleFontSize,
  contentInset = ACTION_CARD_INSET,
  titlePlacement,
  titleTopPercent = 0.45,
  titleMaxLines = 2,
  titleArrowGap,
  footerBottomInset,
  showArrow,
  showTypeIcon,
  onPress,
  style,
}: Readonly<BloomSpaceCardProps>) {
  const { isRTL } = useI18nContext();
  const { breakpointWidth: windowWidth } = useWebViewport();
  const isDetail = variant === 'detail';
  const { isPhone, isTablet, isHubPhoneWeb, isHubMediumWeb } =
    resolveHubLayoutFlags(windowWidth, isDetail);

  const useMidTitleLayout = resolveUseMidTitleLayout(
    isDetail,
    titlePlacement,
    isHubPhoneWeb,
    titleArrowGap,
  );
  const resolvedRadius = resolveCardRadius(borderRadius, isHubPhoneWeb);
  const resolvedInset = resolveHubActionInset(
    contentInset,
    isHubPhoneWeb,
    isHubMediumWeb,
  );
  const typeIconInset = resolveTypeIconInset(
    resolvedInset,
    isHubPhoneWeb,
    isHubMediumWeb,
  );
  const typeIconBadgeSize = resolveTypeIconBadgeSize(
    isHubPhoneWeb,
    isHubMediumWeb,
    isPhone,
  );
  const typeIconGlyphSize = resolveTypeIconGlyphSize(
    isHubPhoneWeb,
    isHubMediumWeb,
    isPhone,
  );
  const resolvedWidth =
    widthProp ?? resolveGridWidth(gridSpan, isPhone, isTablet);
  const cardHeight = resolveDefaultCardHeight(
    heightProp,
    isDetail,
    isPhone,
    gridSpan,
  );
  const withArrow = showArrow ?? variant === 'action';
  const withTypeIcon =
    showTypeIcon ?? (variant === 'action' && Boolean(iconUrl?.trim()));
  const resolvedUrl = useMemo(
    () => resolveBloomUniversalImageUrl(tileImageUrl),
    [tileImageUrl],
  );
  const radiusStyle = useMemo(
    () => ({ borderRadius: resolvedRadius }),
    [resolvedRadius],
  );
  const titleType = useMemo(() => {
    if (titleFontSize != null) {
      /** 100% LH clips Matter ascenders on hub tiles (e.g. medium “Bloom Education”). */
      const lineHeight = Math.max(
        titleFontSize + 4,
        Math.round(titleFontSize * 1.2),
      );
      return { fontSize: titleFontSize, lineHeight };
    }
    return resolveActionTitleSize(gridSpan, windowWidth, isPhone);
  }, [titleFontSize, gridSpan, windowWidth, isPhone]);

  const trimmedIconUrl = iconUrl?.trim() ?? '';
  const typeIcon =
    withTypeIcon && trimmedIconUrl ? (
      <SpaceTypeIconBadge
        iconUrl={trimmedIconUrl}
        isRTL={isRTL}
        typeIconInset={typeIconInset}
        glyphSize={typeIconGlyphSize}
        badgeSize={typeIconBadgeSize}
      />
    ) : null;

  const content = resolveSpaceCardOverlay({
    isDetail,
    title,
    isRTL,
    withArrow,
    titleType,
    resolvedInset,
    useMidTitleLayout,
    titleTopPercent,
    titleMaxLines,
    typeIcon,
    ...(subtitle ? { subtitle } : {}),
    ...(titleFontSize != null ? { titleFontSize } : {}),
    ...(subtitleFontSize != null ? { subtitleFontSize } : {}),
    ...(titleArrowGap != null ? { titleArrowGap } : {}),
    ...(footerBottomInset != null ? { footerBottomInset } : {}),
    ...(arrowIconUrl ? { arrowIconUrl } : {}),
  });

  const cardBody = (
    <ImageBackground
      source={resolvedUrl ? { uri: resolvedUrl } : undefined}
      style={[styles.image, !resolvedUrl && styles.imageFallback]}
      imageStyle={[radiusStyle, styles.imageClip]}
      resizeMode="cover"
    >
      {content}
    </ImageBackground>
  );

  return (
    <View style={[{ width: resolvedWidth, padding: gutter }, style]}>
      <CardChrome
        title={title}
        cardHeight={cardHeight}
        isDetail={isDetail}
        isHubPhoneWeb={isHubPhoneWeb}
        radiusStyle={radiusStyle}
        cardBody={cardBody}
        {...(onPress ? { onPress } : {})}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  actionShadowHost: {
    width: '100%',
    backgroundColor: 'transparent',
    overflow: 'visible',
    // Figma: 0px 8px 24px 0px #00000040 — hub action cards only
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0px 8px 24px 0px ${bloomColors.bloomUniversalActionShadow}` } as ViewStyle)
      : {
          shadowColor: bloomColors.bloomUniversalEyebrow,
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.25,
          shadowRadius: 24,
          elevation: 8,
        }),
  },
  detailShadowHost: {
    width: '100%',
    backgroundColor: 'transparent',
    overflow: 'visible',
  },
  cardChrome: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    position: 'relative',
  },
  actionChrome: {
    borderWidth: StyleSheet.hairlineWidth,
    /** Figma #FFFFFF26 — rgba; 8-digit hex alpha is unreliable on RN Web */
    borderColor: bloomColors.bloomUniversalCardBorder,
  },
  actionChromePhone: {
    borderWidth: 1,
  },
  detailChrome: {
    borderWidth: 1,
    /** Figma #FFFFFF1F */
    borderColor: bloomColors.bloomUniversalDetailBorder,
  },
  detailGradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: DETAIL_GRADIENT_HEIGHT,
    zIndex: 1,
  },
  detailFooterBand: {
    zIndex: 2,
    /** Transparent — dark comes from the continuous gradient behind (avoids a hard band edge) */
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
    paddingBottom: 16,
    paddingTop: 12,
    gap: 8,
  },
  detailInsetHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: bloomColors.bloomUniversalInsetHighlight,
    zIndex: 3,
  },
  pressable: {
    width: '100%',
  },
  pressed: {
    opacity: 0.9,
  },
  /**
   * Fill the chrome even when footer/title are absolute-only (phone mosaic).
   * Otherwise ImageBackground collapses and the bottom→top scrim vanishes.
   */
  image: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
  },
  /** Keep the photo inside the card curve. No square fill behind the radius. */
  imageClip: {
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  imageFallback: {
    backgroundColor: bloomColors.bloomUniversalImageFallback,
  },
  cardGradient: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
  },
  typeIconSlot: {
    position: 'absolute',
    zIndex: 2,
  },
  /** Frosted circle behind Contentful space `icon`. */
  typeIconBadge: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bloomColors.bloomUniversalIconBadge,
  },
  /**
   * Tall + small cards: bottom-aligned stack — title, then arrow on the next row
   * (not centered, not same row as the arrow).
   */
  phoneBottomStackFooter: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  phoneTitleSlot: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 1,
  },
  phoneArrowSlot: {
    position: 'absolute',
    zIndex: 2,
  },
  /**
   * Tall card: title docks above the arrow. The arrow itself is pinned to the
   * card bottom (phoneArrowPinned), so this column only places the title.
   */
  phoneStackFooter: {
    flex: 1,
    position: 'relative',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    alignItems: 'stretch',
    zIndex: 1,
  },
  phoneStackArrow: {
    alignSelf: 'flex-end',
  },
  /**
   * iPad WebKit (Safari and Chrome) can shrink a line-clamped title to 0px in
   * this flex column, leaving the tall card with no visible label.
   */
  phoneStackTitle: {
    flexShrink: 0,
  },
  phoneFooterRtlHost: {
    position: 'relative',
  },
  /** Arabic: physical bottom-left of the phone card footer. */
  phoneArrowPinned: {
    position: 'absolute',
    zIndex: 2,
  },
  actionFooter: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 8,
    zIndex: 1,
  },
  actionFooterRtl: {
    position: 'relative',
  },
  /** Arabic: physical left, independent of the parent RTL row flip. */
  arrowLeft: {
    position: 'absolute',
  },
  actionTitle: {
    flex: 1,
    minWidth: 0,
    fontFamily: 'Matter-Medium',
    fontWeight: '400',
    letterSpacing: 0,
    textAlign: 'left',
  },
  actionTitlePhone: {
    flexGrow: 0,
    flexShrink: 1,
    width: '100%',
    textAlign: 'left',
  },
  /** Font/Size/L Medium · LH 100% · #FFFFFF */
  detailTitle: {
    fontFamily: 'Matter-Medium',
    fontWeight: '400',
    fontSize: 20,
    lineHeight: 20,
    letterSpacing: 0,
    color: bloomColors.bloomUniversalOnPhoto,
  },
  /** Font/Size/S Regular · LH 100% · #F3F1EA */
  detailSubtitle: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 15,
    lineHeight: 15,
    letterSpacing: 0,
    color: bloomColors.bloomUniversalCardSubtitle,
  },
  arrowFlip: {
    transform: [{ scaleX: -1 }],
  },
});

export default BloomSpaceCard;
